import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) return setError("Missing reset token. Open the link from the email again.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");

    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data.token) {
        localStorage.setItem("afr_token", data.token);
        localStorage.setItem("afr_user", JSON.stringify(data.user));
        setUser(data.user);
      }
      toast.success("Password updated");
      navigate("/app", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-shell">
      <div className="px-6 pt-6 pb-2 flex items-center gap-3">
        <Link to="/login" data-testid="reset-back-btn"><ArrowLeft /></Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
          New Password
        </span>
      </div>

      <div className="px-6 pt-4 pb-8">
        <div className="w-14 h-14 rounded-full bg-[#3CFF7E]/10 border border-[#3CFF7E]/40 flex items-center justify-center mb-4">
          <KeyRound className="text-[#3CFF7E]" size={24} />
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-tight">
          Choose a new password
        </h1>
        <p className="text-sm text-[#9CA3AF] mt-2 leading-relaxed">
          At least 6 characters. We'll sign you in straight after.
        </p>
      </div>

      <form onSubmit={submit} className="px-6 space-y-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">New password</label>
          <div className="relative mt-1">
            <input
              type={show ? "text" : "password"}
              required
              autoFocus
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="reset-password-input"
              className="w-full h-12 border border-white/20 rounded-md px-4 pr-10 bg-transparent focus:outline-none focus:border-[#3CFF7E] focus:ring-2 focus:ring-[#3CFF7E]/20"
            />
            <button type="button" onClick={() => setShow((s) => !s)} aria-label="Toggle visibility" className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center text-[#9CA3AF]">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Confirm password</label>
          <input
            type={show ? "text" : "password"}
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            data-testid="reset-confirm-input"
            className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 bg-transparent focus:outline-none focus:border-[#3CFF7E] focus:ring-2 focus:ring-[#3CFF7E]/20"
          />
        </div>

        {error && (
          <div className="text-sm text-[#F87171] border-l-2 border-[#F87171] pl-3" data-testid="reset-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="reset-submit-btn"
          className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md active:scale-[0.98] transition-transform disabled:opacity-60 shadow-[0_0_40px_rgba(60,255,126,0.35)] flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          {loading ? "Updating…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}
